import React from 'react';
import moment from 'moment';
import { titleCase } from 'change-case';

import { generatePaymentLogPDFReceipt } from 'utils/receipt_pdf_generator';
import { trimMoney } from 'utils/strings';

import PaymentLogs from 'app/collections/payment_logs';
import DropdownSelections from 'app/collections/dropdown_selections';
import ApiModeSelection from 'app/models/api_mode_selection';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';
import DropdownSelection from 'app/models/dropdown_selection';
import PaymentLogReport from 'app/models/payment_log_report';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import DateAndTimeHeadingView from 'app/components/list/item/headings/date_and_time';
import PageHeader from 'app/components/page_header_react';
import RequestReportView from 'app/components/request_report';

import tmpl from './template';
import amountHeaderTemplate from './amount_header';
import descriptionHeaderTemplate from './description_header';
import receiptButtonTemplate from './receipt_button';

export const PAGE_SIZE = 10;

export const SOURCE_TYPE_READABLE = {
  bank_account: 'Bank Account Charge',
  credit_card: 'Credit Card Charge',
  insurance: 'Insurance Credit',
  manual_debit: 'Manual Credit',
  manual_credit: 'Manual Debit',
  refund: 'Refund Credit',
  subscription: 'Subscription Payment',
  user: 'Purchase',
  fulfillment_order: 'Order Credit',
};
export const CREDIT_TYPES = ['refund', 'insurance', 'credit_card', 'bank_account', 'manual_debit', 'fulfillment_order'];

export const AmountHeaderView = Base.extend({
  compiledTemplate: amountHeaderTemplate,

  context() {
    const sourceType = this.model.get('source_type');
    return {
      amount: trimMoney(this.model.get('amount'), 'USD'),
      isCredit: CREDIT_TYPES.includes(sourceType),
    };
  },
});

export const DescriptionHeaderView = Base.extend({
  compiledTemplate: descriptionHeaderTemplate,

  context() {
    const sourceType = this.model.get('source_type');
    return {
      sourceType: SOURCE_TYPE_READABLE[sourceType] || sourceType,
      last4: this.model.get('last4'),
    };
  },
});

export const ReceiptButtonView = Base.extend({
  compiledTemplate: receiptButtonTemplate,

  initialize({ user }) {
    this.user = user;
  },

  events: { 'click .download-receipt': 'generateReceipt' },

  context() {
    return {
      receiptButton: this.model.get('status') === 'complete' && this.model.get('target_type') === 'easypost_stripe',
    };
  },

  generateReceipt(event) {
    event.preventDefault();
    const createdDate = moment(this.model.get('created_at') || '').utc();
    const sourceType = this.model.get('source_type');
    if (!createdDate.isValid()) throw new Error('created_at property not present');
    const filename = `easypost_${createdDate.format('YYYYMMDDHHmmss')}_${this.model.id}`;
    generatePaymentLogPDFReceipt(filename, {
      account: this.user.get('email'),
      name: this.user.get('name'),
      date: createdDate.format('YYYY-MM-DD'),
      time: createdDate.format('HH:mm:ss'),
      timezone: 'UTC',
      amount: trimMoney(this.model.get('amount'), 'USD'),
      transactionId: this.model.id,
      paymentMethod: SOURCE_TYPE_READABLE[sourceType] || sourceType,
      last4: this.model.get('last4'),
      plan: this.model.get('plan'),
    });
  },
});

export const buildHeadings = user => [
  new Heading({
    subView: m => new DateAndTimeHeadingView({ model: m }),
    display: 'Date',
    smSize: 2,
    xsHidden: true,
    wrapperClass: 'text-nowrap',
  }),
  new Heading({
    subView: m => new AmountHeaderView({ model: m }),
    display: 'Amount',
    smSize: 2,
    xsSize: 4,
    wrapperClass: 'text-right',
  }),
  new Heading({
    subView: m => new DescriptionHeaderView({ model: m }),
    display: 'Description',
    smSize: 4,
    xsSize: 4,
  }),
  new Heading({
    text: m => {
      const status = titleCase(m.get('status'));
      return (status === 'Creditable') ? 'Pending - Credited' : status;
    },
    display: 'Status',
    smSize: 2,
    xsSize: 4,
  }),
  new Heading({
    subView: m => new ReceiptButtonView({ model: m, user }),
    display: 'Receipt',
    smSize: 2,
    xsHidden: true,
  }),
];

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'payment-logs',

  initialize({ paymentMethods, user, query = {} }) {
    this.user = user;
    this.paymentMethods = paymentMethods;
    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.apiModeSelection = new ApiModeSelection({ mode: 'production' });
    const sourceTypeChoices = [
      { option: '', readable: 'All Transactions' },
      { option: 'user', readable: 'Purchases' },
      { option: 'refund', readable: 'Refund Credits' },
      { option: 'insurance', readable: 'Insurance Credits' },
      { option: 'credit_card', readable: 'Credit Card Charges' },
      { option: 'bank_account', readable: 'Bank Account Charges' },
      { option: 'subscription', readable: 'Subscription Payment' },
    ];
    const userACL = this.user.get('acl') || {};

    if (userACL && userACL.fulfillment) {
      sourceTypeChoices.push({ option: 'fulfillment_order', readable: 'Order Credit' });
    }
    this.sourceType = new DropdownSelection({
      choices: sourceTypeChoices,
      label: 'Transaction Type',
      paramKey: 'source_type',
    });
    this.dropdownSelections = new DropdownSelections(
      [this.dateRange, this.sourceType],
      { initialChoices: query[this.className], urlParamKey: this.className },
    );
    this.collection = new PaymentLogs([], {
      dropdownSelections: this.dropdownSelections,
      pageSize: PAGE_SIZE,
      paymentMethods: this.paymentMethods,
    });

    this.addReactComponent('header', <PageHeader name="Payment Logs" />);
    this.addSubview('paymentLogDaterangePickers', '#payment_log_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));

    this.addSubview('requestPaymentLogReport', '#request_payment_log_report', new RequestReportView({
      ReportClass: PaymentLogReport,
      dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    }));

    this.addSubview('sourceTypeSelector', '#source_type_selector', new SelectorView({ model: this.sourceType }));
    this.addSubview('paymentLogList', '#payment_log_list', new ListView({
      headings: buildHeadings(this.user),
      collection: this.collection,
      noResultsMessage: 'No payment logs are available to view.',
    }));
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/billing/payment_logs/index.jsx